import { useCallback, useRef, useState } from 'react';

import {
  useAcceptDrawMutation,
  useDeclineDrawMutation,
  useOfferDrawMutation,
} from '@features/shared/api/game';

import { IGame } from '../types/game';

export function useDrawOffer({
  playerId,
  customAccessToken,
  game: _game,
  onError: _onError,
}: {
  playerId?: string;
  customAccessToken?: string;
  game?: IGame;
  onError?: (err: Error) => void;
}) {
  const offerDrawMutation = useOfferDrawMutation({ customAccessToken });
  const acceptDrawMutation = useAcceptDrawMutation({ customAccessToken });
  const declineDrawMutation = useDeclineDrawMutation({ customAccessToken });
  //
  const [_registry, setRegistry] = useState<Record<string, unknown>>({});
  //
  const side = _game && playerId ? (_game?.black.id === playerId ? 'black' : 'white') : undefined;
  const showDrawOffer = Boolean(
    side &&
    _game &&
    _game.activeDrawRequest &&
    _game.activeDrawRequest.triggeredBySide.toLowerCase() !== side &&
    _registry[_game.id] !== _game.activeDrawRequest,
  );

  const _store = {
    game: _game,
    registry: _registry,
    offer: offerDrawMutation,
    accept: acceptDrawMutation,
    decline: declineDrawMutation,
    onError: _onError,
  };
  const refStore = useRef(_store);
  refStore.current = _store;

  // ---

  const onDrawOffer = useCallback(() => {
    const { game, offer, onError } = refStore.current;
    offer.mutate(game!.id, { onError });
  }, []);
  const onDrawAccept = useCallback(() => {
    const { registry, game, accept, onError } = refStore.current;
    setRegistry({ [game!.id]: game!.activeDrawRequest, ...registry });
    accept.mutate(game!.id, { onError });
  }, []);
  const onDrawDecline = useCallback(() => {
    const { registry, game, decline, onError } = refStore.current;
    setRegistry({ [game!.id]: game!.activeDrawRequest, ...registry });
    decline.mutate(game!.id, { onError });
  }, []);

  // --

  const canRequestDraw =
    side &&
    _game &&
    _game.moves.length >= 2 &&
    _game[side].drawRequestsLeft > 0 &&
    !_game.activeDrawRequest &&
    _game.status === 'InProgress';

  // --

  return {
    canRequestDraw,
    showDrawOffer,
    onDrawOffer,
    onDrawAccept,
    onDrawDecline,
    submittingDrawOffer: offerDrawMutation.isPending,
    submittingDrawAccept: acceptDrawMutation.isPending,
    submittingDrawDecline: declineDrawMutation.isPending,
  };
}
